import { useState } from 'react';
import {
  BarChart3, TrendingUp, TrendingDown, ArrowUpRight, Clock, Target,
  Ship, Users, AlertTriangle, CheckCircle2, Calendar, Filter,
  Download, Globe2, Layers
} from 'lucide-react';
import { useAppStore } from '../../store/useAppStore';
import { mockFleets } from '../../data/mockData';

function KPIBox({ label, value, sub, trend, color }: { label: string; value: string; sub?: string; trend?: string; color: string }) {
  return (
    <div className="bg-white rounded-2xl p-5 border border-slate-100 hover:shadow-md transition-all">
      <p className="text-sm text-slate-500 mb-1">{label}</p>
      <p className={`text-3xl font-bold ${color}`}>{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
      {trend && <p className="text-xs text-emerald-600 font-medium mt-1">{trend}</p>}
    </div>
  );
}

function SimpleBar({ items, maxVal }: { items: { label: string; value: number; color: string }[]; maxVal: number }) {
  return (
    <div className="space-y-3">
      {items.map(item => (
        <div key={item.label}>
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="text-slate-600">{item.label}</span>
            <span className="font-semibold text-slate-800">{item.value}</span>
          </div>
          <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${(item.value / maxVal) * 100}%`, backgroundColor: item.color }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function LineChartSimple({ data, color, h = 100 }: { data: number[]; color: string; h?: number }) {
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const w = 300;
  const points = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v - min) / range) * h}`).join(' ');
  return (
    <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
      <polyline points={points} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function AnalyticsPage() {
  const { vessels, inspections, correctiveActions } = useAppStore();
  const [period, setPeriod] = useState('6m');

  const avgClosureTime = '18.5';
  const avgInspDuration = '2.3';
  const fleetReadiness = '7.1';
  const recurringDeficiencies = 4;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Fleet Analytics</h1>
          <p className="text-sm text-slate-500 mt-1">Comprehensive insights into fleet performance and inspection trends</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-slate-100 rounded-xl p-1">
            {['1m', '3m', '6m', '1y'].map(p => (
              <button key={p} onClick={() => setPeriod(p)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${period === p ? 'bg-white shadow text-slate-800' : 'text-slate-500'}`}>
                {p}
              </button>
            ))}
          </div>
          <button className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50 flex items-center gap-2">
            <Download className="w-4 h-4" /> Export
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPIBox label="Fleet Readiness Score" value={fleetReadiness} sub="Average RightShip score" trend="↑ +0.3 vs prev period" color="text-sky-600" />
        <KPIBox label="Avg Closure Time" value={`${avgClosureTime}d`} sub="Days to close corrective actions" trend="↓ -2.1 days improvement" color="text-emerald-600" />
        <KPIBox label="Avg Inspection Duration" value={`${avgInspDuration}d`} sub="Days per inspection" trend="Stable" color="text-blue-600" />
        <KPIBox label="Recurring Deficiencies" value={`${recurringDeficiencies}`} sub="Issues found more than once" trend="↑ +1 from last period" color="text-amber-600" />
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Findings by Vessel */}
        <div className="bg-white rounded-2xl p-5 border border-slate-100">
          <h3 className="font-semibold text-slate-800 mb-1">Findings by Vessel</h3>
          <p className="text-xs text-slate-400 mb-4">Open findings per vessel</p>
          <SimpleBar items={vessels.map(v => ({
            label: v.name, value: v.openFindings,
            color: v.openFindings > 8 ? '#EF4444' : v.openFindings > 4 ? '#F59E0B' : '#10B981',
          }))} maxVal={15} />
        </div>

        {/* Common Failure Categories */}
        <div className="bg-white rounded-2xl p-5 border border-slate-100">
          <h3 className="font-semibold text-slate-800 mb-1">Common Failure Categories</h3>
          <p className="text-xs text-slate-400 mb-4">Most frequent finding categories</p>
          <SimpleBar maxVal={15} items={[
            { label: 'Fire Safety', value: 12, color: '#EF4444' },
            { label: 'Documentation', value: 8, color: '#8B5CF6' },
            { label: 'Machinery', value: 9, color: '#F59E0B' },
            { label: 'Environmental', value: 7, color: '#10B981' },
            { label: 'Navigation', value: 5, color: '#3B82F6' },
          ]} />
        </div>

        {/* Compliance Trend */}
        <div className="bg-white rounded-2xl p-5 border border-slate-100">
          <h3 className="font-semibold text-slate-800 mb-1">Compliance Trend</h3>
          <p className="text-xs text-slate-400 mb-4">Fleet compliance % over time</p>
          <LineChartSimple data={[78, 80, 82, 85, 83, 88, 87, 89, 91]} color="#0EA5E9" h={120} />
          <div className="flex justify-between text-[10px] text-slate-400 mt-2">
            {['Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct'].map(m => <span key={m}>{m}</span>)}
          </div>
        </div>

        {/* Closure Performance */}
        <div className="bg-white rounded-2xl p-5 border border-slate-100">
          <h3 className="font-semibold text-slate-800 mb-1">Closure Performance</h3>
          <p className="text-xs text-slate-400 mb-4">Corrective action closure rate</p>
          <LineChartSimple data={[65, 70, 68, 75, 80, 82, 85, 88, 90]} color="#10B981" h={120} />
          <div className="flex justify-between text-[10px] text-slate-400 mt-2">
            {['Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct'].map(m => <span key={m}>{m}</span>)}
          </div>
        </div>
      </div>

      {/* Fleet Comparison Table */}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <div className="p-5 border-b border-slate-100">
          <h3 className="font-semibold text-slate-800">Fleet Performance Comparison</h3>
          <p className="text-xs text-slate-400 mt-0.5">Side-by-side comparison across fleets</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/50">
                <th className="text-left px-5 py-3 font-medium text-slate-500 text-xs">Fleet</th>
                <th className="text-center px-5 py-3 font-medium text-slate-500 text-xs">Vessels</th>
                <th className="text-center px-5 py-3 font-medium text-slate-500 text-xs">Compliance %</th>
                <th className="text-center px-5 py-3 font-medium text-slate-500 text-xs">Avg Score</th>
                <th className="text-center px-5 py-3 font-medium text-slate-500 text-xs">Open Findings</th>
                <th className="text-center px-5 py-3 font-medium text-slate-500 text-xs">Trend</th>
              </tr>
            </thead>
            <tbody>
              {mockFleets.map(fleet => (
                <tr key={fleet.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                  <td className="px-5 py-3.5 font-medium text-slate-800">{fleet.name}</td>
                  <td className="px-5 py-3.5 text-center">{fleet.vesselCount}</td>
                  <td className="px-5 py-3.5 text-center">
                    <span className={`font-semibold ${fleet.complianceRate >= 90 ? 'text-emerald-600' : fleet.complianceRate >= 80 ? 'text-amber-600' : 'text-red-600'}`}>
                      {fleet.complianceRate}%
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-center">
                    <span className="font-semibold text-slate-800">{fleet.avgRightShipScore}</span>
                  </td>
                  <td className="px-5 py-3.5 text-center">
                    <span className={`font-semibold ${fleet.openFindings > 30 ? 'text-red-600' : 'text-amber-600'}`}>
                      {fleet.openFindings}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-center">
                    <span className="text-emerald-600 text-xs font-medium">↑ Improving</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* AI Insights */}
      <div className="bg-gradient-to-r from-[#0A1628] to-[#1E3A5F] rounded-2xl p-6 text-white">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Target className="w-5 h-5 text-amber-400" /> AI-Powered Insights
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { title: 'Risk Prediction', desc: 'MV Phoenix Rising has a 78% predicted probability of RightShip inspection failure based on current deficiency trends and overdue CAPAs.', color: 'bg-red-500/20' },
            { title: 'Pattern Detection', desc: 'Fire safety equipment deficiencies are the #1 recurring issue across the fleet, appearing in 75% of all inspections.', color: 'bg-amber-500/20' },
            { title: 'Optimization', desc: 'Scheduling inspections 2 weeks earlier could reduce average closure time by 3 days based on historical patterns.', color: 'bg-emerald-500/20' },
          ].map(insight => (
            <div key={insight.title} className={`p-4 rounded-xl ${insight.color}`}>
              <h4 className="font-semibold mb-2">{insight.title}</h4>
              <p className="text-sm text-slate-300 leading-relaxed">{insight.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
